import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './RunFilterPanel.css';

const SORT_OPTIONS = [
  { value: 'start_time',              label: 'Date' },
  { value: 'distance_nautical_miles', label: 'Distance (NM)' },
  { value: 'duration_seconds',        label: 'Duration' },
  { value: 'average_speed_knots',     label: 'Avg Speed' },
];

const PANEL_WIDTH = 272;
const PANEL_GAP   = 8;

/* ── Reusable multi-select zone picker ── */
const ZoneMultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close dropdown on outside click — stop propagation so the portal
  // outside-click handler doesn't also fire and close the main panel.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  const toggle = (id) => {
    onChange(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id]
    );
  };

  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  const summary = selected.length === 0
    ? 'Any'
    : selected.length === 1
      ? (options.find((o) => o.id === selected[0])?.name ?? '1 selected')
      : `${selected.length} selected`;

  return (
    <div className="rf-group" ref={ref}>
      <label className="rf-label">{label}</label>
      <div className={`rf-zone-trigger ${open ? 'rf-zone-trigger-open' : ''} ${selected.length > 0 ? 'rf-zone-trigger-active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => !o)}
      >
        <span className="rf-zone-summary">{summary}</span>
        <div className="rf-zone-trigger-icons">
          {selected.length > 0 && (
            <span className="rf-zone-clear" onClick={clearAll} title="Clear" aria-label="Clear selection">✕</span>
          )}
          <svg className={`rf-zone-chevron ${open ? 'rf-zone-chevron-up' : ''}`}
            width="10" height="10" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </div>

      {open && (
        <div className="rf-zone-dropdown">
          {options.length === 0 ? (
            <div className="rf-zone-empty">No zones available</div>
          ) : (
            options.map((z) => (
              <label key={z.id} className="rf-zone-option">
                <input
                  type="checkbox"
                  checked={selected.includes(z.id)}
                  onChange={() => toggle(z.id)}
                />
                <span className="rf-zone-option-name">{z.name}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   Main RunFilterPanel
   ══════════════════════════════════════════════════════════════ */
const RunFilterPanel = ({
  onApply,
  onReset,
  loading     = false,
  geofences   = [],
  resultCount = null,
  isFiltered  = false,
}) => {
  const [isOpen,    setIsOpen]    = useState(false);
  const [panelPos,  setPanelPos]  = useState({ top: 16, left: 344 });

  const toggleRef = useRef(null);
  const panelRef  = useRef(null);

  // ── Filter state ──────────────────────────────────────────────
  const [startDate,       setStartDate]       = useState('');
  const [endDate,         setEndDate]         = useState('');
  const [minDistance,     setMinDistance]     = useState('');
  const [maxDistance,     setMaxDistance]     = useState('');
  const [minDuration,     setMinDuration]     = useState('');
  const [maxDuration,     setMaxDuration]     = useState('');
  const [originZones,     setOriginZones]     = useState([]);  // array of ids
  const [destZones,       setDestZones]       = useState([]);  // array of ids
  const [sortBy,          setSortBy]          = useState('start_time');
  const [sortDir,         setSortDir]         = useState('desc');

  // ── Measure and open ─────────────────────────────────────────
  const openPanel = useCallback(() => {
    if (toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      setPanelPos({ top: Math.max(16, rect.top), left: rect.right + PANEL_GAP });
    }
    setIsOpen(true);
  }, []);

  const handleToggle = () => (isOpen ? setIsOpen(false) : openPanel());

  // ── Close on outside click ────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (!toggleRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [isOpen]);

  // ── Close on resize / scroll ──────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [isOpen]);

  // ── Helpers ───────────────────────────────────────────────────
  const hasActiveFilters = () => !!(
    startDate || endDate ||
    minDistance || maxDistance ||
    minDuration || maxDuration ||
    originZones.length || destZones.length
  );

  const buildFilters = () => {
    const filters = {};
    if (startDate) filters.min_start_time = new Date(startDate).toISOString();
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filters.max_start_time = end.toISOString();
    }
    if (minDistance) filters.min_distance_nautical_miles = minDistance;
    if (maxDistance) filters.max_distance_nautical_miles = maxDistance;
    if (minDuration) filters.min_duration_seconds = parseFloat(minDuration) * 60;
    if (maxDuration) filters.max_duration_seconds = parseFloat(maxDuration) * 60;
    // Pass arrays — useRouteData will append each as a repeated param
    if (originZones.length) filters.origin_zone_ids  = originZones;
    if (destZones.length)   filters.destination_zone_ids = destZones;
    return filters;
  };

  const handleApply = () => {
    onApply({ filters: buildFilters(), sortBy, sortDir });
    setIsOpen(false);
  };

  const handleReset = () => {
    setStartDate('');    setEndDate('');
    setMinDistance('');  setMaxDistance('');
    setMinDuration('');  setMaxDuration('');
    setOriginZones([]);  setDestZones([]);
    setSortBy('start_time');
    setSortDir('desc');
    if (onReset) onReset();
    setIsOpen(false);
  };

  // ── Zone options ──────────────────────────────────────────────
  const zoneOptions = (() => {
    let features = [];
    if (geofences?.type === 'FeatureCollection' && Array.isArray(geofences.features)) {
      features = geofences.features;
    } else if (Array.isArray(geofences)) {
      features = geofences;
    }
    return features
      .filter((f) => f?.properties)
      .map((f) => ({ id: f.properties.id || f.id, name: f.properties.name || 'Unnamed' }));
  })();

  // ── Portal panel ─────────────────────────────────────────────
  const panel = isOpen && createPortal(
    <div ref={panelRef} className="rf-panel"
      style={{ top: panelPos.top, left: panelPos.left, width: PANEL_WIDTH }}>

      <div className="rf-panel-header">
        <span className="rf-panel-title">Filter &amp; Sort</span>
        <button className="rf-panel-close" onClick={() => setIsOpen(false)} aria-label="Close">✕</button>
      </div>

      <div className="rf-panel-body">

        {/* ── Date Range ─────────────────────────── */}
        <div className="rf-group">
          <label className="rf-label">Date Range</label>
          <div className="rf-row">
            <input type="date" className="rf-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span className="rf-separator">–</span>
            <input type="date" className="rf-input" value={endDate}   onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        {/* ── Distance (NM) ──────────────────────── */}
        <div className="rf-group">
          <label className="rf-label">Distance (NM)</label>
          <div className="rf-row">
            <input type="number" className="rf-input" placeholder="Min" min="0" step="0.1" value={minDistance} onChange={(e) => setMinDistance(e.target.value)} />
            <span className="rf-separator">–</span>
            <input type="number" className="rf-input" placeholder="Max" min="0" step="0.1" value={maxDistance} onChange={(e) => setMaxDistance(e.target.value)} />
          </div>
        </div>

        {/* ── Duration (min) ─────────────────────── */}
        <div className="rf-group">
          <label className="rf-label">Duration (minutes)</label>
          <div className="rf-row">
            <input type="number" className="rf-input" placeholder="Min" min="0" step="1" value={minDuration} onChange={(e) => setMinDuration(e.target.value)} />
            <span className="rf-separator">–</span>
            <input type="number" className="rf-input" placeholder="Max" min="0" step="1" value={maxDuration} onChange={(e) => setMaxDuration(e.target.value)} />
          </div>
        </div>

        {/* ── Zone multi-selects ─────────────────── */}
        {zoneOptions.length > 0 && (
          <>
            <ZoneMultiSelect
              label="Origin Zone"
              options={zoneOptions}
              selected={originZones}
              onChange={setOriginZones}
            />
            <ZoneMultiSelect
              label="Destination Zone"
              options={zoneOptions}
              selected={destZones}
              onChange={setDestZones}
            />
          </>
        )}

        {/* ── Sorting ────────────────────────────── */}
        <div className="rf-divider" />

        <div className="rf-group">
          <label className="rf-label">Sort By</label>
          <select className="rf-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>

        <div className="rf-group">
          <label className="rf-label">Order</label>
          <div className="rf-toggle-group">
            <button className={`rf-dir-btn ${sortDir === 'asc'  ? 'rf-dir-active' : ''}`} onClick={() => setSortDir('asc')} >↑ Ascending</button>
            <button className={`rf-dir-btn ${sortDir === 'desc' ? 'rf-dir-active' : ''}`} onClick={() => setSortDir('desc')}>↓ Descending</button>
          </div>
        </div>

      </div>

      <div className="rf-panel-footer">
        <button className="rf-btn rf-btn-reset" onClick={handleReset} disabled={loading}>Reset</button>
        <button className="rf-btn rf-btn-apply" onClick={handleApply} disabled={loading}>
          {loading ? <><span className="rf-spinner" />Searching…</> : 'Apply Filters'}
        </button>
      </div>
    </div>,
    document.body
  );

  return (
    <div className="rf-container">
      <button
        ref={toggleRef}
        className={[
          'rf-toggle',
          isOpen                           ? 'rf-toggle-active'   : '',
          hasActiveFilters() || isFiltered ? 'rf-toggle-filtered' : '',
        ].filter(Boolean).join(' ')}
        onClick={handleToggle}
        title="Filter & sort runs"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        <span>Filter Routes</span>
        {(hasActiveFilters() || isFiltered) && <span className="rf-badge" />}
        {resultCount !== null && <span className="rf-count">{resultCount}</span>}
      </button>
      {panel}
    </div>
  );
};

export default RunFilterPanel;